import type { InspectorItem, InspectorTabs } from '@/kova/spec/types'

import type { DataSource } from './provenance'

/**
 * The inspector for a screen that reads the backend.
 *
 * # Why a live screen cannot keep the corpus inspector
 *
 * `DEFAULT_INSP` is transcribed verbatim from the prototype and every value in
 * it is invented — including, in tab one, a heading that says **Provenance**
 * over `postgres · ledger_postings` and `read at 2026-07-31 14:02 UTC`. It also
 * carries a customer name, an IBAN, a `decision_ref` and a provider event id.
 *
 * On a corpus screen that is fine: the screen says it is corpus and the panel
 * is part of the same drawing. On a live screen it is the worst thing in the
 * console. The header chip says **Live**, and eighteen inches to the right a
 * panel whose own title is "Provenance" states a source and a read time that
 * nobody produced. An operator has no way to know the chip answers for the
 * middle of the screen and not for the panel — and the two figures most likely
 * to be copied out of this console and quoted at someone are a `decision_ref`
 * and a read timestamp, which is exactly what that panel offers.
 *
 * # What replaces it
 *
 * Tab one becomes real. A live screen already knows its store, its authority
 * and its read time — that is what `DataSource` is — so the Provenance tab is
 * the one part of the inspector we can answer honestly, and answering it is
 * better than hiding it.
 *
 * The other three say they are not wired, each naming what would fill it. They
 * are not removed: the panel is 316px of the layout, and collapsing it would
 * move every block on the screen. Same rule as the withheld balance legs — an
 * absence is shown as an absence, never closed up as though it were never
 * there.
 */

/**
 * One provenance row.
 *
 * `InspectorItem` is `[key, value, font]` — the inspector's own tuple, NOT the
 * six-wide `KvItem` the `kv` BLOCK uses. They are different shapes with the
 * same name in conversation, and mixing them is a type error the project's
 * `tsc -b` catches and a bare `tsc --noEmit` does not.
 */
function kv(key: string, value: string, font: '' | 'm' = ''): InspectorItem {
  return [key, value, font]
}

/**
 * Builds the inspector for a live screen from its provenance.
 *
 * Takes the `DataSource` rather than reaching for the query, so it is pure and
 * table-testable (CLAUDE.md Part III) and so it cannot render a claim that
 * disagrees with the chip in the header — both read the same value.
 *
 * `null` means the read is still in flight. It is a separate argument from any
 * `DataSource` variant because "we have not asked yet" and "we asked and were
 * told nothing" are different sentences, and the panel is titled Provenance:
 * the one place in the console where being vague about which of those it is
 * would be worst.
 */
export function liveInspector(source: DataSource | null): InspectorTabs {
  return [
    [{ k: 'kv', title: 'Provenance', items: provenanceRows(source) }],
    [
      {
        k: 'kv',
        title: 'Activity',
        items: [
          kv(
            'not wired',
            'The audit trail for this screen is not read yet. It exists in go-wallet — this console does not ask for it.',
          ),
        ],
      },
    ],
    [
      {
        k: 'kv',
        title: 'Related records',
        items: [
          kv(
            'not wired',
            'A live screen has no selected row to relate anything to yet. Nothing is shown rather than the design’s example customer, case and decision reference.',
          ),
        ],
      },
    ],
    [
      {
        k: 'kv',
        title: 'Raw payload',
        items: [
          kv(
            'not wired',
            'The raw provider event is not requested for this screen. The design’s example payload is a fabricated FinXP event and must not stand in for one.',
          ),
        ],
      },
    ],
  ]
}

/**
 * The Provenance tab, in the corpus tab's own shape and order.
 *
 * `legal` is stated on every variant and does not come from the payload: it is
 * a constant of this system (invariant 2). Our ledger is the operational system
 * of record and FinXP's records win every discrepancy — an operator reading a
 * balance here needs to know which of the two they are looking at before they
 * quote it to a customer.
 */
function provenanceRows(source: DataSource | null): readonly InspectorItem[] {
  const legal = kv('legal', "FinXP's records are legally authoritative")

  if (source === null) {
    return [
      kv('source', 'reading…'),
      kv('authority', 'not known until the read lands'),
      legal,
    ]
  }

  if (source.kind === 'unread') {
    // What we TRIED and what came back, and no `read at` row at all. A blank
    // "read at" beside a store name reads as a read that returned nothing;
    // omitting the row is the only rendering that does not imply one happened.
    return [
      kv('source', `${source.source} — attempted, no answer`, 'm'),
      kv('authority', 'none — nothing on this screen was read'),
      kv('why', source.reason),
      legal,
    ]
  }

  if (source.kind === 'probe') {
    // No `read at` row, for the same reason `unread` has none: a blank stamp
    // beside a store name reads as a read that returned nothing. The difference
    // from `unread` is the WHY row — there, something was attempted and failed;
    // here, nothing has been attempted, and that is the ordinary state of this
    // screen rather than a fault to diagnose.
    return [
      kv('source', `${source.source} — not called yet`, 'm'),
      kv('authority', 'none yet — this screen reads nothing until you run it'),
      kv('why', source.reason),
      legal,
    ]
  }

  if (source.kind === 'not_built') {
    // Same shape as the corpus branch below and for the same reason, but it can
    // state WHY rather than only that it does not know: the screen is designed
    // and unconnected, which is a fact worth carrying into the panel an
    // operator opens when they want to know where a figure came from.
    return [
      kv('source', 'none — this screen is not built yet', 'm'),
      kv('authority', 'none claimed'),
      kv('why', source.reason),
      legal,
    ]
  }

  if (source.kind === 'corpus') {
    // Reachable only if a caller asks for a live inspector over a corpus
    // screen, which is a bug. It renders as a refusal to claim anything rather
    // than as an empty panel, because an empty Provenance tab reads as "no
    // provenance was needed" instead of "this console lost track".
    return [
      kv('source', 'unknown — this screen is not wired'),
      kv('authority', 'none claimed'),
      legal,
    ]
  }

  const rows: InspectorItem[] = [
    kv('source', source.source, 'm'),
    kv('authority', source.authority),
    legal,
    kv('read at', source.readAt === '' ? 'not stamped by the backend' : source.readAt, 'm'),
  ]

  if (source.kind === 'partial') {
    rows.push(
      kv(
        'not answered',
        source.missing.length === 0
          ? 'Some figures were not answered for, and the backend did not say which.'
          : source.missing.join(' · '),
      ),
    )
  }

  return rows
}
